import type { EspaceOverview } from "@shared/espace.types";
import { HABITUDE_MINIMUM, habitudesDe, resumeHabitudes } from "@shared/habitudes";
import { Card, CardHeader } from "@/components/ds/Card";

const MOIS = [
	"jan",
	"fév",
	"mar",
	"avr",
	"mai",
	"juin",
	"juil",
	"août",
	"sep",
	"oct",
	"nov",
	"déc",
];

/**
 * Ce que l'entreprise apprend sur elle-même sans avoir rien rempli.
 *
 * Le bloc n'apparaît qu'au-delà de trois dépôts, et c'est une règle de fond : deux
 * offres ne font pas une habitude. Un graphe à deux barres et une moyenne calculée
 * sur une mesure donneraient l'air d'une analyse là où il n'y a qu'un échantillon.
 *
 * ⚠️ Aucun compteur de vues ni de clics. JobTeaser en affiche ; nous ne les avons
 * pas — les offres vivent ici, les étudiants dans main-app, et rien ne relie encore
 * les deux. Un compteur à zéro, ou pire un chiffre inventé, coûterait exactement la
 * confiance que ce bloc cherche à gagner.
 */
export function HabitudesCard({ overview }: { overview: EspaceOverview }) {
	const habitudes = habitudesDe(overview.requests, overview.selections);
	const resume = resumeHabitudes(habitudes);
	if (!resume) return null;

	const maxSaison = Math.max(...habitudes.saison.map((point) => point.nombre), 1);

	return (
		<Card padded={false}>
			<CardHeader
				title="Vos habitudes de recrutement"
				subtitle={`Calculées sur vos ${habitudes.base} offres. Rien à remplir.`}
			/>
			<div className="p-6">
				<p className="t-body-lg text-mg-heading">{resume}</p>

				<div className="mt-5 grid gap-5 sm:grid-cols-2">
					<div>
						<p className="t-caption text-mg-secondary">Types de contrat</p>
						<ul className="mt-2 flex flex-col gap-1.5">
							{habitudes.contrats.map((contrat) => (
								<li key={contrat.valeur} className="flex items-center gap-3">
									<span className="t-body w-32 shrink-0 truncate text-mg-body">
										{contrat.valeur}
									</span>
									{/*
									 * `accent` et pas `primary` : ce sont des données CATÉGORIELLES,
									 * pas une action. C'est la règle du référentiel, et primary sur
									 * un graphe le fait ressembler à quelque chose de cliquable.
									 */}
									<span
										className="h-2 rounded-full bg-mg-accent"
										style={{ width: `${(contrat.nombre / habitudes.base) * 100}%`, minWidth: 8 }}
									/>
									<span className="t-caption tabular-nums text-mg-secondary">{contrat.nombre}</span>
								</li>
							))}
						</ul>
					</div>

					<div>
						<p className="t-caption text-mg-secondary">Quand vous déposez</p>
						{/*
						 * ⚠️ Chaque colonne prend `h-full`, et la barre est dans un bloc de
						 * hauteur DÉFINIE. Une hauteur en pourcentage dans un parent à
						 * hauteur automatique vaut zéro : le graphe s'affichait vide, avec
						 * ses douze libellés et aucune barre.
						 */}
						<ul className="mt-2 flex h-24 items-stretch gap-1">
							{MOIS.map((nom, index) => {
								const point = habitudes.saison.find((entree) => entree.mois === index);
								const hauteur = point ? (point.nombre / maxSaison) * 100 : 0;
								return (
									<li key={nom} className="flex h-full flex-1 flex-col justify-end gap-1">
										<span
											className={`w-full rounded-t-sm ${point ? "bg-mg-accent" : "bg-mg-subtle"}`}
											style={{ height: `${Math.max(hauteur, 5)}%` }}
											title={`${nom} : ${point?.nombre ?? 0} offre${(point?.nombre ?? 0) > 1 ? "s" : ""}`}
										/>
										<span className="t-caption text-center text-mg-secondary">{nom}</span>
									</li>
								);
							})}
						</ul>
					</div>
				</div>

				{/*
				 * Le délai de l'école est le SEUL chiffre que l'entreprise ne peut pas
				 * calculer elle-même, et celui qu'elle veut connaître avant de déposer.
				 * Il n'apparaît qu'une fois une offre tranchée : « 0 jour » sur zéro
				 * mesure serait un mensonge.
				 */}
				{habitudes.delaiEcoleJours !== null || habitudes.profilsRecus > 0 ? (
					<div className="mt-5 flex flex-wrap gap-x-8 gap-y-3 border-t border-mg-border-subtle pt-4">
						{habitudes.delaiEcoleJours !== null ? (
							<Chiffre
								valeur={`${habitudes.delaiEcoleJours.toLocaleString("fr-FR", { maximumFractionDigits: 1 })}\u202Fj`}
								label="pour que l'école réponde, en moyenne"
							/>
						) : null}
						{habitudes.profilsRecus > 0 ? (
							<>
								<Chiffre valeur={String(habitudes.profilsRecus)} label="profils reçus" />
								<Chiffre
									valeur={String(habitudes.profilsRetenus)}
									label="que vous avez voulu rencontrer"
								/>
							</>
						) : null}
					</div>
				) : null}
			</div>
		</Card>
	);
}

function Chiffre({ valeur, label }: { valeur: string; label: string }) {
	return (
		<div>
			<p className="t-h3 tabular-nums text-mg-heading">{valeur}</p>
			<p className="t-caption mt-0.5 text-mg-secondary">{label}</p>
		</div>
	);
}

export { HABITUDE_MINIMUM };
